// Pure metric math for the Writing Dashboard (spec §3.2). Everything is derived
// from stored **totals** (the book's total word count as of each local day), so it's
// multi-device correct. A day's words are its total minus where it started: its
// recorded start (#1049), else the day before's total. A week or month adds up its days,
// each floored at 0, so a day of cutting counts as 0 and never lowers the days after it
// (#1047). No live-editor value is used here.

export interface WritingActivity {
  startTotal: number | null;
  days: Record<string, number>; // "YYYY-MM-DD" (local date) -> total word count that day
  /** "YYYY-MM-DD" -> the count when that day's first session opened (#1049). Absent on
   *  days recorded before it existed. */
  dayStarts?: Record<string, number>;
  dailyWordGoal: number | null;
}

export interface DayDelta {
  date: string;
  delta: number; // signed daily words written
}

export interface WritingMetrics {
  today: number;   // words written today (floored at 0)
  week: number;    // this week, from most recent Monday: each day floored at 0, summed
  month: number;   // this calendar month: each day floored at 0, summed
  streak: number;  // consecutive counting days
  goal: number | null;
  todaySigned: number;   // true signed dailyWritten(today) — for goal progress
  goalMetToday: boolean;
  perDay: DayDelta[];    // last 30 local days, true signed deltas
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Format a Date as a local YYYY-MM-DD. */
export function fmtLocal(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The viewer's local "today" as YYYY-MM-DD. */
export function localToday(): string {
  return fmtLocal(new Date());
}

/** Add `n` days to a YYYY-MM-DD string (local), returning YYYY-MM-DD. */
export function addDays(date: string, n: number): string {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + n);
  return fmtLocal(d);
}

const dayBefore = (date: string) => addDays(date, -1);

/** Total word count as of date D: the value for the greatest recorded date ≤ D,
 *  else the anchor `startTotal` (0 if unset). */
export function totalAsOf(act: WritingActivity, date: string): number {
  let best: string | null = null;
  for (const d in act.days) {
    if (d <= date && (best === null || d > best)) best = d;
  }
  if (best !== null) return act.days[best];
  return act.startTotal ?? 0;
}

/** Signed words written on date D: from where the day started when that was recorded,
 *  so a gap between visits (a recount, cuts made elsewhere) is charged to no day (#1049);
 *  else from the day before's total. */
export function dailyWritten(act: WritingActivity, date: string): number {
  const start = act.dayStarts?.[date];
  const total = act.days[date];
  if (start != null && total != null) return total - start;
  return totalAsOf(act, date) - totalAsOf(act, dayBefore(date));
}

/** Words written from `start` to `end`, both included: each day floored at 0, then
 *  summed. A negative day counts as 0 on its own day only (#1047). */
export function writtenBetween(act: WritingActivity, start: string, end: string): number {
  let sum = 0;
  for (let d = start; d <= end; d = addDays(d, 1)) sum += Math.max(0, dailyWritten(act, d));
  return sum;
}

/** Most recent Monday ≤ `today` (ISO week; WEEK_START = Monday). */
export function weekStartMonday(today: string): string {
  const dow = new Date(`${today}T00:00:00`).getDay(); // 0=Sun … 6=Sat
  return addDays(today, -((dow + 6) % 7));
}

/** First of `today`'s local month. */
export function monthStart(today: string): string {
  return `${today.slice(0, 8)}01`;
}

export function computeMetrics(act: WritingActivity, today: string = localToday()): WritingMetrics {
  const written = (windowStart: string) => writtenBetween(act, windowStart, today);

  const goal = act.dailyWordGoal ?? null;
  const hasGoal = goal !== null && goal > 0;
  const counts = (d: string) => (hasGoal ? dailyWritten(act, d) >= (goal as number) : dailyWritten(act, d) > 0);

  // Streak: walk back from today. An unfinished today never breaks yesterday's
  // streak — if today doesn't count yet, start the walk at yesterday. Terminates
  // because dailyWritten becomes 0 once we walk past all recorded data.
  let streak = 0;
  let cursor = counts(today) ? today : dayBefore(today);
  let guard = 0;
  while (counts(cursor) && guard < 3660) {
    streak++;
    cursor = dayBefore(cursor);
    guard++;
  }

  const perDay: DayDelta[] = [];
  for (let i = 29; i >= 0; i--) {
    const d = addDays(today, -i);
    perDay.push({ date: d, delta: dailyWritten(act, d) });
  }

  const todaySigned = dailyWritten(act, today);
  return {
    today: written(today),
    week: written(weekStartMonday(today)),
    month: written(monthStart(today)),
    streak,
    goal,
    todaySigned,
    goalMetToday: hasGoal ? todaySigned >= (goal as number) : false,
    perDay,
  };
}
